import {
  GridView,
  PurchaseHistory,
  Profile,
  ListView,
  CreditCard,
  Heart,
} from '@one-thd/sui-icons';
import { ProXtraIcon } from '../icons/pro-xtra';
import { INavRoute, IRouteGroup } from '../models/navigation';

export const routes = {
  purchaseHistory: {
    path: '/myaccount/purchase-history',
    label: 'Purchase History',
    external: true,
    shouldShow: true,
    analyticsName: 'purchaseHistory',
  },
  buyItAgain: {
    path: '/buyitagain',
    label: 'Buy It Again',
    external: true,
    shouldShow: true,
    analyticsName: 'buyItAgain',
  },
  b2cSubscriptions: {
    path: '/myaccount/ordersubscription',
    label: 'Subscriptions',
    external: true,
    shouldShow: true,
    analyticsName: 'subscriptions',
  },
  subscriptions: {
    path: '/myaccount/ordersubscription',
    label: 'Subscriptions',
    external: true,
    shouldShow: true,
    analyticsName: 'subscriptions',
  },
  businessTools: {
    path: '/myaccount/business-tools',
    label: 'Track Business Spend',
    external: true,
    shouldShow: true,
    analyticsName: 'trackBusinessSpend',
  },
  downloads: {
    path: '/myaccount/downloads',
    label: 'Downloads',
    external: true,
    shouldShow: true,
    analyticsName: 'downloads',
  },
  proXtraRewardsHub: {
    path: '/myaccount/proXtraRewards',
    label: 'Rewards Hub',
    external: true,
    shouldShow: (
      loyaltyMembershipDetails,
      userState,
      customer,
      featuresMap,
    ) => {
      return featuresMap?.ENABLE_REWARDS_HUB_PAGE;
    },
    analyticsName: 'rewardsHub',
  },
  pricingBenefits: {
    path: '/myaccount/proXtraPricing',
    label: 'Pricing Benefits',
    external: true,
    shouldShow: true,
    analyticsName: 'pricingBenefits',
  },
  projects: {
    path: '/projects',
    label: 'Projects',
    external: true,
    shouldShow: (
      loyaltyMembershipDetails,
      userState,
      customer,
      featuresMap,
    ) => {
      if (!featuresMap.ENABLE_PROJECTS_PAGE) {
        return false;
      }

      if (featuresMap.disableProjectBenefitCheck) {
        return customer?.customerType?.toUpperCase() === 'B2B';
      }

      return !!loyaltyMembershipDetails?.proTiers?.benefitTags?.find(
        ({ tagName }) => tagName === 'ProjectBenefit',
      );
    },
    analyticsName: 'projects',
  },
  jobQuotes: {
    path: (features) => {
      return features.ENABLE_NUCLEUS_QUOTE_EXPERIENCE
        ? '/quote/list'
        : '/b2b/account/view/quote/list';
    },
    label: 'Job Quotes',
    external: true,
    shouldShow: true,
    analyticsName: 'jobQuotes',
  },
  poJobNames: {
    path: '/myaccount/pojobnames',
    label: 'PO/Job Names',
    external: true,
    shouldShow: (loyaltyMembershipDetails, userState, customer) => {
      return customer?.role?.toUpperCase() === 'ADMIN';
    },
    analyticsName: 'poJobNames',
  },
  productLists: {
    path: '/list/view/summary',
    label: 'Product Lists',
    external: true,
    shouldShow: true,
    analyticsName: 'productLists',
  },
  profile: {
    path: '/myaccount/profile',
    label: 'Profile',
    external: true,
    shouldShow: true,
    analyticsName: 'profile',
  },
  b2cMobileMenu: {
    path: '/myaccount/profile/menu',
    label: 'Profile',
    external: true,
    shouldShow: true,
    analyticsName: 'profile',
  },
  dashboard: {
    path: '/myaccount/dashboard',
    label: 'Account',
    external: true,
    shouldShow: true,
    analyticsName: 'account',
  },
  b2cAddresses: {
    path: '/myaccount/addresses',
    label: 'Addresses',
    external: true,
    shouldShow: true,
    analyticsName: 'addresses',
  },
  addresses: {
    path: '/myaccount/addresses',
    label: 'Addresses',
    external: true,
    shouldShow: true,
    analyticsName: 'addresses',
  },
  militaryDiscount: {
    path: '/military/discount',
    label: 'Military Discount Benefit',
    external: true,
    shouldShow: true,
    analyticsName: 'militaryDiscountBenefit',
  },
  addManageUsers: {
    path: '/myaccount/addmanageusers',
    label: 'Add/Manage Users',
    external: true,
    shouldShow: (loyaltyMembershipDetails, userState, customer) => {
      return customer?.role?.toUpperCase() === 'ADMIN';
    },
    analyticsName: 'addManageUsers',
  },
  securityPassword: {
    path: '/myaccount/security',
    label: 'Security & Password',
    external: true,
    shouldShow: true,
    analyticsName: 'securityPassword',
  },
  b2cNotificationPreferences: {
    path: '/myaccount/preferences',
    label: 'Communication Preferences',
    external: true,
    shouldShow: true,
    analyticsName: 'communicationPreferences',
  },
  notificationPreferences: {
    path: '/myaccount/preferences',
    label: 'Communication Preferences',
    external: true,
    shouldShow: true,
    analyticsName: 'communicationPreferences',
  },
  linkedAccounts: {
    path: '/myaccount/linked-accounts',
    label: 'Linked Accounts',
    external: true,
    shouldShow: (loyaltyMembershipDetails, userState, customer) => {
      return (
        !!(
          userState?.customer?.hierarchyOrgId || userState?.customer?.companyId
        ) && customer?.role?.toUpperCase() === 'ADMIN'
      );
    },
    analyticsName: 'linkedAccounts',
  },
  b2cCardsAccounts: {
    path: '/myaccount/payments',
    label: 'Cards & Accounts',
    external: true,
    shouldShow: true,
    analyticsName: 'cardsAccounts',
  },
  cardsAccounts: {
    path: '/myaccount/payments',
    label: 'Cards & Accounts',
    external: true,
    shouldShow: true,
    analyticsName: 'cardsAccounts',
  },
  b2cHomeDepotCreditCard: {
    path: '/myaccount/thdcreditcard',
    label: 'Home Depot Credit Cards',
    external: true,
    shouldShow: true,
    analyticsName: 'homeDepotCreditCards',
  },
  homeDepotCreditCard: {
    path: '/myaccount/thdcreditcard',
    label: 'Home Depot Credit Cards',
    external: true,
    shouldShow: true,
    analyticsName: 'homeDepotCreditCards',
  },
  giftCards: {
    path: '/c/Gift_Cards',
    label: 'Gift Cards',
    external: true,
    shouldShow: true,
    analyticsName: 'giftCards',
  },
  proAllowanceManagement: {
    path: (features) =>
      features.ENABLE_NEW_PRO_ALLOWANCE
        ? '/myaccount/proallowance'
        : '/b2b/account/view/manageProAllowances',
    label: 'Pro Allowance Management',
    external: true,
    shouldShow: (loyaltyMembershipDetails, userState, customer) => {
      return (
        customer?.role?.toUpperCase() === 'ADMIN' &&
        !!loyaltyMembershipDetails?.proTiers?.benefitTags?.find(
          ({ tagName }) => {
            return tagName === 'runnerAllowance';
          },
        )
      );
    },
    analyticsName: 'proAllowanceManagement',
  },
  instantCheckout: {
    path: '/myaccount/payments/instant-checkout',
    label: 'Instant Checkout',
    external: true,
    shouldShow: true,
    analyticsName: 'instantCheckout',
  },
  windowTreatments: {
    path: 'https://install.homedepot.com/MyWindowTreatments',
    label: 'Window Treatments',
    external: true,
    shouldShow: true,
    analyticsName: 'windowTreatments',
  },
  pathToProNetwork: {
    path: '/path-to-pro-network',
    label: 'Path to Pro Network',
    external: true,
    shouldShow: true,
    analyticsName: 'pathToProNetwork',
  },
  paintPurchaseHistory: {
    path: '/myaccount/paintPurchaseHistory',
    label: 'Paint Purchase History',
    external: true,
    shouldShow: true,
    analyticsName: 'paintPurchaseHistory',
  },
} as const satisfies Record<string, INavRoute>;

export const dashboardRoute: IRouteGroup = {
  title: 'Account Dashboard',
  icon: GridView,
  links: [],
};

export const routeGroups: IRouteGroup[] = [
  {
    title: 'Purchases',
    icon: PurchaseHistory,
    links: [
      routes.purchaseHistory,
      routes.paintPurchaseHistory,
      routes.b2cSubscriptions,
    ],
  },
  {
    title: 'Payment Methods',
    icon: CreditCard,
    links: [
      routes.b2cCardsAccounts,
      routes.b2cHomeDepotCreditCard,
      routes.instantCheckout,
    ],
  },
  {
    title: 'Account Settings',
    icon: Profile,
    links: [
      routes.profile,
      routes.b2cAddresses,
      routes.militaryDiscount,
      routes.securityPassword,
      routes.b2cNotificationPreferences,
    ],
  },
  {
    title: 'Plan Your Projects',
    icon: Heart,
    links: [routes.productLists, routes.windowTreatments],
  },
];

export const b2bRouteGroups: IRouteGroup[] = [
  {
    title: 'Purchases',
    icon: PurchaseHistory,
    links: [
      routes.purchaseHistory,
      routes.paintPurchaseHistory,
      routes.buyItAgain,
      routes.subscriptions,
      routes.businessTools,
      routes.downloads,
    ],
  },
  {
    title: 'Pro Xtra Program',
    icon: ProXtraIcon,
    links: [
      routes.proXtraRewardsHub,
      routes.pricingBenefits,
      routes.pathToProNetwork,
    ],
  },
  {
    title: 'Plan Your Jobs',
    icon: ListView,
    links: [
      routes.projects,
      routes.jobQuotes,
      routes.poJobNames,
      routes.productLists,
    ],
  },
  {
    title: 'Account Settings',
    icon: Profile,
    links: [
      routes.profile,
      routes.addresses,
      routes.militaryDiscount,
      routes.addManageUsers,
      routes.securityPassword,
      routes.notificationPreferences,
      routes.linkedAccounts,
    ],
  },
  {
    title: 'Payment Methods',
    icon: CreditCard,
    links: [
      routes.cardsAccounts,
      routes.homeDepotCreditCard,
      routes.proAllowanceManagement,
    ],
  },
];

export const storeFrontRouteGroups: IRouteGroup[] = [
  {
    title: 'Purchases',
    icon: PurchaseHistory,
    links: [routes.buyItAgain],
  },
  {
    title: 'Plan Your Jobs',
    icon: ListView,
    links: [routes.productLists],
  },
];
